'use client';

import { X } from 'lucide-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import Button from '../shared/Button';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Toast } from '../../components/shared/Toast';
import Tooltip from '../shared/Tooltip';

export default function EditProfileModal({ isOpen, onClose, userProfile, onSaveProfile }) {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [photo, setPhoto] = useState('');
	const [selectedImage, setSelectedImage] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const fileInputRef = useRef(null);
	const [isLoading, setIsLoading] = useState(false); // Add isLoading state

	const hasChangesDetected = useMemo(() => {
		const firstNameChanged = firstName.trim() !== (userProfile.firstName || '').trim();
		const lastNameChanged = lastName.trim() !== (userProfile.lastName || '').trim();
		const photoChanged = selectedImage !== null;
		return firstNameChanged || lastNameChanged || photoChanged;
	}, [firstName, lastName, selectedImage, userProfile]);

	useEffect(() => {
		if (userProfile) {
			setFirstName(userProfile.firstName || '');
			setLastName(userProfile.lastName || '');
			setPhoto(userProfile.photo || '');
			setSelectedImage(null); // Clear selected image when userProfile changes
			setPreviewUrl(null); // Clear preview URL
		}
	}, [userProfile, isOpen]);

	useEffect(() => {
		if (previewUrl || photo) {
			const img = new Image();
			img.src = previewUrl || photo;
			img.onload = () => setIsImageLoaded(true);
		} else {
			setIsImageLoaded(true);
		}
	}, [photo, previewUrl]);

	if (!isOpen) return null;

	const handleBackdropMouseDown = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const handleImageUpload = () => {
		fileInputRef.current?.click();
	};

	const handleFileSelect = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Validate file type
			const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
			if (!allowedTypes.includes(file.type)) {
				Toast.error('Upload a JPG, PNG, OR WebP file (max 1 MB, 1024X1024 px)');
				event.target.value = ''; // Reset file input
				return;
			}

			// Validate file size (1MB = 1024 * 1024 bytes)
			if (file.size > 1024 * 1024) {
				Toast.error('Upload a JPG, PNG, OR WebP file (max 1 MB, 1024X1024 px)');
				event.target.value = ''; // Reset file input
				return;
			}

			// Validate image dimensions (max 1024x1024)
			const objectUrl = URL.createObjectURL(file);
			const img = new Image();
			img.onload = () => {
				const isTooLarge = img.naturalWidth > 1024 || img.naturalHeight > 1024;
				if (isTooLarge) {
					Toast.error('Upload a JPG, PNG, OR WebP file (max 1 MB, 1024X1024 px)');
					URL.revokeObjectURL(objectUrl);
					event.target.value = ''; // Reset file input
					return;
				}
				setSelectedImage(file);
				setPreviewUrl(objectUrl);
				setIsImageLoaded(false);
			};
			img.onerror = () => {
				Toast.error('Failed to load image. Please try a different file.');
				URL.revokeObjectURL(objectUrl);
				event.target.value = ''; // Reset file input
			};
			img.src = objectUrl;
		} else {
			setSelectedImage(null);
			setPreviewUrl(null);
		}
	};

	const handleFirstNameChange = (e) => {
		setFirstName(e.target.value);
	};

	const handleLastNameChange = (e) => {
		setLastName(e.target.value);
	};

	const handleSave = async () => {
		if (!firstName) {
			Toast.error('Please enter your first name');
			return;
		}

		if (!lastName) {
			Toast.error('Please enter your last name');
			return;
		}

		// Validate firstName length
		if (firstName.trim().length < 3) {
			Toast.error('Minimum length for first name is 3 characters');
			return;
		}

		// Validate lastName length
		if (lastName.trim().length < 3) {
			Toast.error('Minimum length for last name is 3 characters');
			return;
		}

		setIsLoading(true);
		try {
			// Pass the selected image file along with other user data
			await onSaveProfile({
				firstName,
				lastName,
				photo: selectedImage || photo,
				profileImage: selectedImage // This will be used for the API call
			});
		} finally {
			setIsLoading(false);
			onClose();
		}
	};

	const InfoTooltip = () => (
		<span className="ml-1 inline-flex items-center justify-center w-[12px] h-[12px] rounded-full border border-[#180A63] text-[#180A63] text-[10px] font-semibold leading-none">
			?
		</span>
	);

	return (
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
			onMouseDown={handleBackdropMouseDown}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/70" />

			{/* Modal */}
			<div
				className="relative z-10 w-full max-w-[500px] rounded-[30px] bg-white shadow-2xl p-[50px]"
				onMouseDown={(e) => e.stopPropagation()}
			>
				{/* Close button */}
				<button
					onClick={onClose}
					className="absolute top-3 right-3 sm:top-4 sm:right-4 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{/* Heading */}
				<div className="flex items-center">
					<h2 className="text-xl sm:text-2xl font-bold text-dark-blue mb-3">Edit Profile</h2>
					<Tooltip content="Update your name and email information.">
						<InfoTooltip />
					</Tooltip>
				</div>
				<p className="text-gray text-sm sm:text-base mb-8">Update your name and profile photo</p>

				{/* Profile photo section */}
				<div className="flex sm:flex-row flex-col items-center gap-3 mb-10">
					<div className="relative w-[100px] h-[100px] rounded-full overflow-hidden flex-shrink-0 border border-lightGrey">
						{!isImageLoaded ? (
							<Skeleton
								height="100%"
								width="100%"
								baseColor="#E5E7EB"
								highlightColor="#F3F4F6"
								duration={1.2}
								circle
							/>
						) : (
							<img src={previewUrl || photo} alt="Profile" className="w-full h-full object-cover" />
						)}
					</div>
					<div className="sm:text-left text-center">
						<div className="flex items-center">
							<p className="text-dark-blue font-medium text-sm mb-1">Change Profile Photo</p>
							<Tooltip content="Upload a photo to personalize your profile. Your initials are shown if no photo is added.">
								<InfoTooltip />
							</Tooltip>
						</div>

						<p className="text-[12px] text-gray mb-2 italic">
							Upload a JPG, PNG, OR WebP file (max 1 MB, 1024X1024 px)
						</p>

						<Button
							onClick={handleImageUpload}
							variant="outline-gradient-text"
							className="!w-auto !px-4 h-[30px] rounded-[6px] text-xs font-medium"
						>
							Change Profile Photo
						</Button>

						{/* Hidden file input */}
						<input
							ref={fileInputRef}
							type="file"
							accept="image/jpeg,image/png,image/webp"
							onChange={handleFileSelect}
							className="hidden"
						/>
					</div>
				</div>

				{/* Input fields */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-[50px]">
					{/* First Name */}
					<div>
						<div className="flex items-center gap-2 mb-2">
							<label htmlFor="first-name" className="text-sm font-medium text-dark-blue">
								First Name <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Enter the first name you want displayed on your account. Letters, spaces, hyphens, and apostrophes only.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<input
							type="text"
							id="first-name"
							value={firstName}
							onChange={handleFirstNameChange}
							className="w-full rounded-[10px] border border-gray px-4 py-3 text-sm outline-none"
						/>
					</div>

					{/* Last Name */}
					<div>
						<div className="flex items-center gap-2 mb-2">
							<label htmlFor="last-name" className="text-sm font-medium text-dark-blue">
								Last Name <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Enter the last name you want displayed on your account. Letters, spaces, hyphens, and apostrophes only.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<input
							type="text"
							id="last-name"
							value={lastName}
							onChange={handleLastNameChange}
							className="w-full rounded-[10px] border border-gray px-4 py-3 text-sm outline-none"
						/>
					</div>
					<div className="sm:col-span-2 opacity-50">
						<div className="flex items-center gap-2 mb-2">
							<label htmlFor="email-address" className="text-sm font-medium text-dark-blue">
								Email Address <span className="text-red-500">*</span>
							</label>
							<Tooltip
								content="This email is used for login and account notifications."
								wrapperClassName="inline-block"
							>
								<InfoTooltip />
							</Tooltip>
						</div>
						<input
							type="email"
							id="email-address"
							name="email-address"
							value={userProfile.email}
							readOnly
							className="w-full rounded-[10px] border border-gray px-4 py-3 text-sm outline-none"
						/>
					</div>
				</div>

				{/* Buttons */}
				<div className="flex gap-3">
					<Tooltip
						content="Discard your changes and return to profile settings."
						wrapperClassName="flex-1"
					>
						<Button
							onClick={onClose}
							variant="outline-gradient-text"
							className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-sm sm:text-base"
						>
							Cancel
						</Button>
					</Tooltip>
					<Tooltip content="Save your updated profile details." wrapperClassName="flex-1">
						<Button
							onClick={handleSave}
							disabled={!hasChangesDetected || isLoading}
							className="flex-1 h-[45px] sm:h-[48px] !py-2.5 rounded-[10px] text-white text-sm sm:text-base"
						>
							{isLoading ? 'Saving...' : 'Save Changes'}
						</Button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
}
